import { useEffect, useRef, useState } from 'react';
import graph from '@utils/graph';
import db from '@utils/db';
import Bugsnag from '@utils/bugsnag';
import { getFreshToken, isInIframe, pauseRenewalsUntilSignIn, reloadToSignInAgain, resumeRenewals, setTokenRefresher, tokenExpiresSoon } from '@utils/tokenRefresh';
import { useStore } from '@store/appStore';
import { Client } from '@store/partials/client';
import ClientSettings from '@models/ClientSettings';
import { AuthService } from '@libs/teams-auth';
import { shallow } from 'zustand/shallow';
import persistentStore from '@store/persistentStore';
import moment from 'moment/moment';
import { useI18n } from 'i18next-react';
import { ensureLanguageLoaded } from '@i18n/lazyLanguages';
import { useRouter } from 'next/router';
import OnScreenDebugger from '@utils/OnScreenDebuuger';
import pkceChallenge from "pkce-challenge";
import { ViewColoring, ViewIndicators } from '@models/Accessibility';
import * as MSAL from '@azure/msal-browser';

/**
 * Shared state + helpers used by BOTH the desktop/teams AuthProvider and the
 * mobile AuthProvider.
 *
 * NOT included (kept in each provider on purpose - genuinely different token
 * sources / auth libraries per platform, not just drift):
 * - login / logout / getToken / loginMobile / getProfilePic
 *
 * Passed in as parameters because other shared functions here need to call
 * them, even though their implementation stays provider-specific:
 * - getProfile (also used directly by checkToken/getClientData below),
 *   instance (from each provider's own useMsal() call, used by checkToken
 *   and getAccessToken).
 */

// The demo school's client in backendws (GetClient_APP_Settings) - see getDemoData().
const DEMO_CLIENT_ID = 3;

// Login often runs the moment a device wakes up or switches network (UCN 2026-10-07: every call failed
// without leaving the device for ~5s, then worked). axios only retries for ~1.5s and callApi then hands
// back null, which the login code used to run straight into (`cSettings.filter`) and leave the user on
// the loader. These are the calls login can't do without, so they get a longer wait.
const LOGIN_DATA_RETRY_DELAYS_MS = [2000, 4000, 8000];

async function loadLoginData<T>(load: () => Promise<T>): Promise<T | null> {
  for (let attempt = 0; ; attempt++) {
    const result = await load().catch(() => null);
    if (result != null) return result;
    if (attempt >= LOGIN_DATA_RETRY_DELAYS_MS.length) return null;
    await new Promise((resolve) => setTimeout(resolve, LOGIN_DATA_RETRY_DELAYS_MS[attempt]));
  }
}

export function useAuthBase({ mobile, isDemo, teams, isMultiLogin, getProfile, instance }: {
  mobile: boolean;
  isDemo: boolean;
  teams: boolean;
  isMultiLogin: boolean;
  // Still provider-specific (see note above) - getDemoData/assignGroups need
  // it, so it's passed in rather than duplicated here too.
  getProfile: () => Promise<any>;
  // From useMsal() in each provider component - can't call the hook itself
  // from inside this one, so the value is passed through instead.
  instance: any;
}) {
  const [demo, setDemo] = useState(false);
  const [error, seterror] = useState(null);
  const scopes = ['user.read', 'openid', 'profile', 'email'];
  const router = useRouter();

  async function getAccessToken(account) {
    return new Promise(async (resolve, reject) => {
      let response;
      try {
        response = await instance.acquireTokenSilent({
          account: account,
          scopes: scopes,
        });

        if (response.accessToken) {
          resolve(response.accessToken);
        } else {
          reject(Error('Failed to acquire an access token'));
        }
      } catch (error) {
        // in case if silent token acquisition fails, fallback to an interactive method
        if (error instanceof MSAL.InteractionRequiredAuthError) {
          if (isInIframe()) {
            // Redirects are refused inside an iframe (Bugsnag: redirect_in_iframe at ucsyd), and
            // this promise used to never settle then. Ask the user to sign in again via the popup
            // prompt instead, and carry on without a token until they do.
            useStore.getState().setSessionExpired(true);
            resolve(null);
            return;
          }
          /**
           * This will cause the app to leave the current page and redirect to the consent screen.
           * Once consent is provided, the app will return back to the current page and then the
           * silent token acquisition will succeed.
           */
          await instance.acquireTokenRedirect({
            scopes: scopes,
          });
        }
      }
    });
  }

  const {
    client,
    setClientData,
    setSettings,
    settings,
    setWhoIsLoggedIn,
    setTutorial,
    setGroups,
    groups,
    token,
    settoken,
    setMobileLogin,
    mobileLogin,
    setMenuOpen,
    setSafeArea,
    reset,
    setTenantId,
    setClientSettings,
    setSystemSettings,
    systemSettings,
    setSemesters,
    ids,
    setIds,
    user,
    setUser,
    initialRegistrationTypes,
    setInitialRegistrationTypes,
    hideAbsenceMenu,
    setHideAbsenceMenu,
    setCallNameSetting,
    setCaseWorkerId,
    setAlerts,
    caseWorkerEmail,
    setCaseWorkerEmail,
    schoolName,
    setSchoolName,
    setSelectedCaseworker,
    wondeTags,
    setWondeTags,
    multilogin,
    setMultiLogin,
    setOtherUser,
    otherUser,
    whoIsLoggedIn,
    setSchools,
    setGuardianStudents,
    guardianStudents,
    filterDate,
    schools,
    setNotificationCounter,
    schoolViewFilter,
    setSchoolViewFilter,
    setSchoolViewDateRange,
    selectedSemester,
    setSelectedSemester,
    kohortsAvailable,
    setKohortsAvailable,
    setPopupLogin,
    setActive,
    active,
    apiUrl,
    setApiUrl,
  } = useStore(
    (state) => ({
      client: state.client,
      setClientData: state.setClientData,
      setSettings: state.setSettings,
      settings: state.settings,
      setWhoIsLoggedIn: state.setWhoIsLoggedIn,
      setTutorial: state.setTutorial,
      groups: state.groups,
      setGroups: state.setGroups,
      token: state.token,
      settoken: state.settoken,
      mobileLogin: state.mobileLogin,
      setMobileLogin: state.setMobileLogin,
      setMenuOpen: state.setMenuOpen,
      setSafeArea: state.setSafeArea,
      reset: state.reset,
      setTenantId: state.setTenantId,
      setClientSettings: state.setClientSettings,
      setSystemSettings: state.setSystemSettings,
      systemSettings: state.systemSettings,
      setSemesters: state.setSemesters,
      ids: state.ids,
      setIds: state.setIds,
      setUser: state.setUser,
      user: state.user,
      initialRegistrationTypes: state.initialRegistrationTypes,
      setInitialRegistrationTypes: state.setInitialRegistrationTypes,
      hideAbsenceMenu: state.hideAbsenceMenu,
      setHideAbsenceMenu: state.setHideAbsenceMenu,
      setCallNameSetting: state.setCallNameSetting,
      setCaseWorkerId: state.setCaseWorkerId,
      setAlerts: state.setAlerts,
      caseWorkerEmail: state.caseWorkerEmail,
      setCaseWorkerEmail: state.setCaseWorkerEmail,
      schoolName: state.schoolName,
      setSchoolName: state.setSchoolName,
      setSelectedCaseworker: state.setSelectedCaseworker,
      wondeTags: state.wondeTags,
      setWondeTags: state.setWondeTags,
      multilogin: state.multilogin,
      setMultiLogin: state.setMultiLogin,
      setOtherUser: state.setOtherUser,
      otherUser: state.otherUser,
      whoIsLoggedIn: state.whoIsLoggedIn,
      setSchools: state.setSchools,
      setGuardianStudents: state.setGuardianStudents,
      guardianStudents: state.guardianStudents,
      filterDate: state.filterDate,
      schools: state.schools,
      setNotificationCounter: state.setNotificationCounter,
      schoolViewFilter: state.schoolViewFilter,
      setSchoolViewFilter: state.setSchoolViewFilter,
      setSchoolViewDateRange: state.setSchoolViewDateRange,
      selectedSemester: state.selectedSemester,
      setSelectedSemester: state.setSelectedSemester,
      kohortsAvailable: state.kohortsAvailable,
      setKohortsAvailable: state.setKohortsAvailable,
      setPopupLogin: state.setPopupLogin,
      setActive: state.setActive,
      active: state.active,
      apiUrl: state.apiUrl,
      setApiUrl: state.setApiUrl,
    }),
    shallow,
  );

  const { setPersistentStore } = persistentStore();
  const [authenticator, setAuthenticator]: [AuthService, any] = useState(null);
  const i18n = useI18n();
  const [loggingIn, setlogginIn] = useState(false);

  async function initUser() {
    if (settings.TEAMS && settings.TEAMS === 'true') {
      let userData;
      try {
        userData = await authenticator.getUser();
      } catch (error) {
        // Teams refused a token (no consent, timeout, ...) - report it with Teams' reason and show
        // the login error, instead of leaving the user on the loader.
        Bugsnag.notify(error);
        seterror({
          code: 401,
          title: 'login-error-title',
          message: 'login-error-message',
        });
        return;
      }

      if (userData) {
        setUser(userData);
        return;
      }
    }
  }

  useEffect(() => {
    if (authenticator && teams) {
      initUser();
    }
  }, [authenticator]);

  // Lets the db layer silently swap in a new token before the login one expires. Silent only:
  // getAccessToken()'s interactive fallback would redirect the page away from under a background
  // request. Mobile sends user.access_token instead of `token`, so isn't covered here.
  // Set when the last silent renewal failed because Microsoft wants the user to sign in again
  // (refresh token expired) - nothing silent can fix that, see the visibility handler below.
  const loginRequired = useRef(false);

  useEffect(() => {
    if (mobile) return;
    const isTeams = teams || settings?.TEAMS === 'true';
    setTokenRefresher(async () => {
      if (isTeams) return authenticator ? await authenticator.getToken(scopes) : null;
      if (!instance || !user) return null;
      try {
        const response = await instance.acquireTokenSilent({ account: user, scopes });
        loginRequired.current = false;
        // Silent renewal works again (e.g. they signed in again in another tab) - drop the prompt.
        if (useStore.getState().sessionExpired) useStore.getState().setSessionExpired(false);
        return response?.accessToken ?? null;
      } catch (e) {
        if (e instanceof MSAL.InteractionRequiredAuthError) {
          loginRequired.current = true;
          // Retrying silently can't fix this - stop hammering the token endpoint until they sign in.
          pauseRenewalsUntilSignIn();
          // While the user is actively here: ask them to sign in again (SessionExpiredPrompt) rather
          // than redirecting away - they may be in the middle of something. Coming back to a hidden
          // tab is handled by the visibility handler below.
          if (document.visibilityState === 'visible') useStore.getState().setSessionExpired(true);
        }
        throw e;
      }
    });
    return () => setTokenRefresher(null);
  }, [mobile, teams, settings?.TEAMS, authenticator, instance, user]);

  // The prompt's "Sign in again" button: a popup, so it works inside iframes too and the page (and
  // anything typed on it) stays as it is. Must run from the click - browsers block popups otherwise.
  async function signInAgain() {
    if (!instance || !user) return;
    try {
      const response = await instance.acquireTokenPopup({ account: user, scopes, loginHint: user.username });
      if (response?.accessToken) {
        settoken(response.accessToken);
        loginRequired.current = false;
        resumeRenewals();
        useStore.getState().setSessionExpired(false);
      }
    } catch (e) {
      // Popup closed or blocked - leave the prompt up so they can try again.
      Bugsnag.leaveBreadcrumb('Sign-in-again popup failed', { error: String((e as any)?.errorCode ?? e) });
    }
  }

  // A tab hidden overnight / a laptop waking from sleep comes back with a long-expired token, and
  // its polling timers fire straight away - renew it as soon as the tab is visible again, before
  // they do (only does anything when the token is actually expired or about to be). If even that
  // can't be done silently, reload to sign in again (web only - Teams' token comes from the Teams
  // client). Only here, when the user has just come back, never from a background request. In an
  // iframe (no redirects possible), or right after an automatic reload already happened, show the
  // "sign in again" prompt instead.
  useEffect(() => {
    if (mobile) return;
    const isTeams = teams || settings?.TEAMS === 'true';
    const onVisible = async () => {
      if (document.visibilityState !== 'visible') return;
      const current = await getFreshToken();
      if (!isTeams && loginRequired.current && current && tokenExpiresSoon(current)) {
        if (isInIframe() || !reloadToSignInAgain()) useStore.getState().setSessionExpired(true);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mobile, teams, settings?.TEAMS]);

  // Tells Bugsnag who's affected - without it, it counted "users" by IP, so a whole school behind
  // one network looked like one user. Deliberately no name/email/student number (students, often
  // minors; third-party service): the Entra object id is stable per person but only meaningful
  // inside the school's tenant. School + roles go in as metadata for filtering. Cleared on logout.
  useEffect(() => {
    const objectId = user?.localAccountId;
    if (!objectId || !client) {
      if (!user) {
        Bugsnag.setUser(undefined);
        Bugsnag.clearMetadata('school');
        Bugsnag.clearMetadata('user');
      }
      return;
    }

    const roleGroups: [string, string | undefined][] = [
      ['student', client.StudentGroupName],
      ['teacher', client.RegisterGroupName],
      ['admin', client.AdminGroupName],
      ['guestTeacher', client.GuestTeacherGroupName],
      ['schoolLeader', client.SchoolLeaderGroupName],
      ['secretary', client.SchoolSecretaryGroupName],
      ['caseWorker', client.CaseWorkerGroupName],
      ['guardian', client.GuardianGroupName],
      ['superAdmin', client.SuperAdminGroupName],
    ];
    const roles = roleGroups.filter(([, group]) => group && groups?.includes(group)).map(([role]) => role);

    Bugsnag.setUser(objectId);
    Bugsnag.addMetadata('school', { clientId: client.client_id, name: schoolName ?? null });
    Bugsnag.addMetadata('user', { roles: roles.join(', ') || 'none' });
  }, [user, client, groups, schoolName]);


  const getId = () => {
    let adminGroup = client.AdminGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let teacherGroup = client.RegisterGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let studentGroup = client.StudentGroupNameSrc?.toLowerCase().replace(/\s/g, '');

    if (groups.includes(adminGroup)) return settings.DEMO === true || settings.DEMO === 'true' ? 422 : ids.teacher_id;
    else if (groups.includes(teacherGroup)) return settings.DEMO === true || settings.DEMO === 'true' ? 422 : ids.teacher_id;
    else if (groups.includes(studentGroup)) return settings.DEMO === true || settings.DEMO === 'true' ? 60249 : ids.student_id;
  };


  const getStudentId = () => {
    let studentGroup = client.StudentGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    if (groups.includes(studentGroup)) return settings.DEMO === true || settings.DEMO === 'true' ? 60249 : ids.student_id;
  };


  const getType = () => {
    let adminGroup = client.AdminGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let teacherGroup = client.RegisterGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let studentGroup = client.StudentGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let caseworkerGroup = client.CaseWorkerGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let schoolLeaderGroup = client.SchoolLeaderGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let guestteacher = client.GuestTeacherGroupName;

    if (groups.includes(adminGroup)) return 2;
    else if (groups.includes(teacherGroup)) return 1;
    else if (groups.includes(studentGroup)) return 3;
    else if (groups.includes(caseworkerGroup)) return 3;
    else if (groups.includes(schoolLeaderGroup)) return 2
    else if (groups.includes(guestteacher)) return 2
  };


  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }


  function setCookie(name, value, days) {
    let expires = '';
    if (days) {
      let date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    document.cookie = name + '=' + (value || '') + expires + '; path=/';
  }


  function delete_cookie(name) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  }


  async function loginMitId(redirect_uri) {
    let pkce = await pkceChallenge();
    localStorage.setItem('pkce', JSON.stringify(pkce))
    let url = "https://aroscloud.sandbox.signicat.com/auth/open/connect/authorize?&client_id=sandbox-fancy-tooth-102&response_type=code&redirect_uri=" + redirect_uri + "/login&state=1721201415569-91h&scope=openid%20nin%20profile&code_challenge=" +
      pkce.code_challenge +
      "&code_challenge_method=S256&prompt=login"
    router.replace(url)
  }


  async function getTokenTeams(scope) {
    return authenticator.getToken(scope);
  }

  // Used to call UnmanagedApi's getClientSettings, but that endpoint just returns one of 3
  // hardcoded JSON blobs unrelated to the actual deployed environment - of its fields, only
  // GA_TRACKING_ID and ENABLE_PRIVACY_SETTING were ever read, and getClientData() overwrites
  // everything else moments later anyway. Builds the same shape locally instead - this only
  // exists to get `settings` out of its initial null state before getClientData() enriches it.
  // DEMO_ENV is the one field with no other source of truth (unlike the rest, getClientData()
  // never sets it) - it's what the desktop provider's settings effect checks to decide whether
  // to run the demo bypass (getDemoData()) instead of attempting a real MSAL login.
  // Builds on whatever settings are already in the store instead of replacing them: called while
  // getClientData()'s complete settings were already there, it used to wipe them back to just these
  // flags (USE_LESSON_BASED_ABSENCE, images, coins, merit/pause... gone - HeatMap crashed on it).
  async function awaitSettings() {
    const settings = { ...(useStore.getState().settings ?? {}) } as ClientSettings;
    // All three of getClientSettings' blobs had this 'true', and nothing else sets it for a real school:
    // without it the registration views hide the percentages for everyone and ignore the privacy toggle
    // (only the demo, which sets it itself, kept working).
    settings.ENABLE_PRIVACY_SETTING = 'true';
    if (teams) settings.TEAMS = 'true';
    if (isDemo) settings.DEMO_ENV = 'true';
    setSettings(settings);
    return settings;
  }

  // Byte-for-byte identical between desktop/mobile already - no reconciliation needed.
  async function getMitidToken(url) {
    let verifier = JSON.parse(localStorage.getItem('pkce'))
    if (verifier && verifier.code_verifier) {
      let text = router.asPath
      let t = text.split('code=')
      let x = t[1].split('&')
      let token = await db.RedeemAuthorizationCode(x[0], verifier.code_verifier, url).catch(e => {
        router.push('/')
        seterror({
          code: 403,
          title: 'not-authorized-title',
          message: 'not-authorized-message',
        });
      })
      if (token?.data?.access_token) {
        let cprData = await db.GetUserinfoMitId(token.data.access_token)
        if (cprData?.data?.nin) {
          let usernameData = { username: cprData.data.nin + "@aroscloud.dk" }
          let temp = Object.assign(usernameData, cprData.data)
          let schools = await db.CheckMitidLogin(cprData?.data?.nin)
          sessionStorage.setItem('loginType', 'mitId')
          if (schools?.length > 0) {
            setCookie('user', JSON.stringify({ username: schools[0].guid, schools: schools, name: temp.name }), 365)
            setOtherUser(temp)
            setSchools(schools)
          }
          else {
            router.push('/')
            seterror({
              code: 403,
              title: 'not-authorized-title',
              message: 'not-authorized-message',
            });
          }
        }
      }
      else {
        router.push('/')
        setMultiLogin(1)
      }
    }
    else {
      router.push('/')
    }
  }

  // Byte-for-byte identical between desktop/mobile already - no reconciliation needed.
  async function getWondeTags() {
    let tags = await db.WondeGetAllTags()
    let present = []
    let absent = []
    let late = []

    tags.forEach(t => {
      if (t.parent_code === null) {
        switch (t.type) {
          case "PRESENT":
            if (t.code === "L") {
              t.disable = true
              t.active = true
              late.push(t)
            }
            else if (t.code === "/" || t.code === "\\") {
              t.active = true;
              t.disable = true
              //present.push(t)
            }
            else present.push(t)
            break;
          case "AUTHORISED":
          case "UNAUTHORISED":
            absent.push(t)
            break;
          default:
            break;
        }
      }
    });

    present = present.filter(t => t.active)
    absent = absent.filter(t => t.active)
    late = late.filter(t => t.active)
    setWondeTags({ present, absent, late })
  }

  useEffect(() => {
    if (settings?.USEWONDE) {
      getWondeTags()
    }
  }, [settings])

  // Was 29/30 lines identical - the one differing line was the same
  // setTutorial-commented-out-on-desktop-vs-live-on-mobile pattern already
  // reconciled in assignGroups/getDemoData; used mobile's live version here too.
  async function assignGroupsMitId() {
    OnScreenDebugger.instance.log('assign groups mitid');
    const groupArr = [];

    groupArr.push(client.GuardianGroupName?.toLowerCase().replace(/\s/g, ''));

    if (!settings.DEMO) {
      setGroups(groupArr);
      setWhoIsLoggedIn(user.name, '');
      if (groupArr.length === 0) {
        setTutorial(false);
        seterror({
          code: 403,
          title: 'not-authorized-title',
          message: 'not-authorized-message',
        });
      } else {
        let tutorial = null;
        localStorage.setItem('login', 'true');
        setCookie('login', 'true', 14);
        let showTutorial = systemSettings.find((t) => t.APP_Setting_ID === 51);

        if (showTutorial && showTutorial.value.toLowerCase() === 'true') {
          tutorial = await db.getClientTutorialOptions(user.username);
          if (tutorial?.[0]) setTutorial(tutorial[0].showstartupscreen);
        }
      }
    }
  }

  // Textually identical between desktop/mobile already - the only reason it
  // stayed duplicated before was that it calls getAccessToken(), which is
  // provider-specific and now passed in as a parameter instead.
  async function checkToken() {
    if (settings.TEAMS && settings.TEAMS === 'true')
      if (!authenticator) return false;
      else if (!instance && !user) return false;

    if (!token && !settings.DEMO) {
      const t = settings.TEAMS && settings.TEAMS === 'true' ? await authenticator.getToken(scopes) : await getAccessToken(user);
      settoken(t);
      return true;
    }
    return true;
  }

  // Was near-identical already (only a debug console.log and the api_url
  // storage differed) - a naive line-position diff made this and
  // setupUserSettings look far more different than they actually were,
  // since one extra/missing line near the top throws off every line after
  // it. Found and fixed a real bug in the process: mobile wrote the
  // per-client api_url override to localStorage, but callApi() in
  // src/utils/db/index.ts only ever reads it from sessionStorage - so that
  // override silently never took effect on mobile. Kept mobile's added
  // setApiUrl() store update (harmless, arguably an improvement) and
  // switched to sessionStorage to match what's actually read; dropped the
  // ineffective localStorage write plus two debug console.logs (desktop only).
  // Still no answer after loadLoginData's wait - the login error page instead of a crash on null and a
  // loader that never goes away (same as a failed group lookup).
  function loginDataUnavailable(what: string) {
    const error = new Error(`Login data could not be loaded: ${what}`);
    error.name = 'LoginDataUnavailable';
    Bugsnag.notify(error, (event) => {
      event.severity = 'warning';
    });
    seterror({
      code: 503,
      title: 'login-error-title',
      message: 'login-error-message',
    });
  }

  async function getClientData() {
    OnScreenDebugger.instance.log('clientdata');
    if (user.username) {
      let url = window.location.host
      let clientData = await loadLoginData(() => db.getClientData(user.username, url));
      if (!clientData) return loginDataUnavailable('getClientData');
      setTenantId(user.tenantId);
      let temp = await loadLoginData(() => db.getAllClientSettings(user.tenantId));
      if (!temp) return loginDataUnavailable('getAllClientSettings');
      let currentOffset = "+02:00";

      if (temp.length > 0) {
        setSchoolName(temp[0].customer_name)
        currentOffset = temp[0].currentoffset;
      }

      let offset = parseInt(currentOffset.split(':')[0]) * 60 + parseInt(currentOffset.split(':')[1])

      let cSettings = await loadLoginData(() => db.getAllClientSetting(clientData.client_id));
      if (!cSettings) return loginDataUnavailable('getAllClientSetting');
      let sysSettings = cSettings.filter((s) => s.system);
      let studentNote = sysSettings.find((s) => s.APP_Setting_ID === 9);
      let lessonBasedAbsence = sysSettings.find((s) => s.APP_Setting_ID === 8);
      let timezone = sysSettings.find((s) => s.APP_Setting_ID === 7);
      let useCoins = sysSettings.find((s) => s.APP_Setting_ID === 21);
      let useBadges = sysSettings.find((s) => s.APP_Setting_ID === 22);
      let useStreaks = sysSettings.find((s) => s.APP_Setting_ID === 23);
      let useRewards = sysSettings.find((s) => s.APP_Setting_ID === 57);
      let useWonde = sysSettings.find((s) => s.APP_Setting_ID === 63);
      let combineIdWithDisplayname = sysSettings.find((s) => s.APP_Setting_ID === 64);
      let useMerit = clientData.AllowMerit
      let usePause = clientData.AllowPause;
      let useNotFullBlockPause = false;
      let saveSemesters = sysSettings.find((s) => s.APP_Setting_ID === 87);
      let allowCustomDates = sysSettings.find((s) => s.APP_Setting_ID === 90);
      let defaultPeriodType = sysSettings.find((s) => s.APP_Setting_ID === 92);

      let showPresence = sysSettings.find((s) => s.APP_Setting_ID === 79);
      let lowAbsenceThreshhold = sysSettings.find((s) => s.APP_Setting_ID === 80);
      let mediumAbsenceThreshhold = sysSettings.find((s) => s.APP_Setting_ID === 82);
      let highAbsenceThreshhold = sysSettings.find((s) => s.APP_Setting_ID === 83);

      let colorSettings = { low: lowAbsenceThreshhold.value, medium: mediumAbsenceThreshhold.value, high: highAbsenceThreshhold.value }


      // Never overwrite a working URL with an empty one (sessionStorage would store it as the text
      // "null"/"undefined", and every direct call would go to "<that>/<endpoint>" on the site itself).
      if (clientData?.ws_api_url) {
        setApiUrl(clientData.ws_api_url);
        sessionStorage.setItem('api_url', clientData.ws_api_url);
      }
      let initialRegistrationTypes = await db.GetInitialsRegistrations_Types();
      if (initialRegistrationTypes) setInitialRegistrationTypes(initialRegistrationTypes);

      if (usePause) {
        let t = await db.Check4NotFulldays()
        if (t?.length > 0 && t[0].active) useNotFullBlockPause = true
      }

      if (!(await setupUserSettings(clientData.UserName, clientData.client_id, clientData.Domain))) return;

      let s = undefined;
      if (!settings) {
        s = await awaitSettings();
        s.IMAGE_SAS_TOKEN = clientData.picture_token;
        s.IMAGE_URL = clientData.picture_url_small;
        s.USER_IMAGE_URL = clientData.picture_url_large;
        s.STUDENTNOTEHIDDEN = studentNote.value.toLowerCase() !== 'true';
        s.USE_LESSON_BASED_ABSENCE = lessonBasedAbsence.value.toLowerCase();
        s.TIMEZONE = timezone.value;
        s.USECOINS = useCoins.value.toLowerCase() === 'true';
        s.USEBADGES = useBadges.value.toLowerCase() === 'true';
        s.USESTREAKS = useStreaks.value.toLowerCase() === 'true';
        s.USEREWARDS = useRewards.value.toLowerCase() === 'true';
        s.USEMERIT = useMerit;
        s.USEPAUSE = usePause;
        s.USENOTFULLBLOCKPAUSE = useNotFullBlockPause
        s.USEWONDE = useWonde.value.toLowerCase() === 'true';
        s.COMBINEID = combineIdWithDisplayname.value.toLowerCase() === 'true'
        s.COLORSETTINGS = colorSettings
        s.SHOWPRESENCE = showPresence.value === 'true'
        s.SAVESEMESTERS = saveSemesters.value === 'true'
        s.ALLOWCUSTOMDATES = allowCustomDates?.value === 'true'
        s.DEFAULTPERIODTYPE = defaultPeriodType?.value
        s.CURRENTOFFSET = offset;
        s.AUTOTEAMSREADY = clientData.AutoTeamsReady;
        setSettings(s);
      } else {
        // Latest from the store, not this function's `settings` (captured when it started, possibly
        // superseded by another setSettings since) - same for the other copies below.
        s = JSON.parse(JSON.stringify(useStore.getState().settings ?? settings));
        s.ENABLE_PRIVACY_SETTING = 'true'; // see awaitSettings()
        s.IMAGE_SAS_TOKEN = clientData.picture_token;
        s.IMAGE_URL = clientData.picture_url_small;
        s.USER_IMAGE_URL = clientData.picture_url_large;
        s.STUDENTNOTEHIDDEN = studentNote.value.toLowerCase() !== 'true';
        s.USE_LESSON_BASED_ABSENCE = lessonBasedAbsence.value.toLowerCase();
        s.TIMEZONE = timezone.value;
        s.USECOINS = useCoins.value.toLowerCase() === 'true';
        s.USEBADGES = useBadges.value.toLowerCase() === 'true';
        s.USESTREAKS = useStreaks.value.toLowerCase() === 'true';
        s.USEREWARDS = useRewards.value.toLowerCase() === 'true';
        s.USEMERIT = useMerit;
        s.USEPAUSE = usePause;
        s.USENOTFULLBLOCKPAUSE = useNotFullBlockPause
        s.USEWONDE = useWonde.value.toLowerCase() === 'true';
        s.COMBINEID = combineIdWithDisplayname.value.toLowerCase() === 'true'
        s.COLORSETTINGS = colorSettings
        s.SHOWPRESENCE = showPresence.value === 'true'
        s.SAVESEMESTERS = saveSemesters.value === 'true'
        s.ALLOWCUSTOMDATES = allowCustomDates?.value === 'true'
        s.DEFAULTPERIODTYPE = defaultPeriodType?.value
        s.CURRENTOFFSET = offset;
        s.AUTOTEAMSREADY = clientData.AutoTeamsReady;
        setSettings(s);
      }

      if (clientData.AdminGroupName === '' && clientData.RegisterGroupName === '' && clientData.StudentGroupName === '') {
        const latestSettings = useStore.getState().settings ?? settings;
        if (latestSettings) s = JSON.parse(JSON.stringify(latestSettings));
        s.KOHORTACTIVE = true;
        s.DEMO = true;
        s.IMAGE_SAS_TOKEN = clientData.picture_token;
        s.IMAGE_URL = clientData.picture_url_small;
        s.USER_IMAGE_URL = clientData.picture_url_large;
        s.USECOINS = true;
        s.USEBADGES = true;
        s.USESTREAKS = true;
        s.USEREWARDS = true;
        s.USEPAUSE = false;
        s.USEMERIT = false
        s.COLORSETTINGS = colorSettings
        s.ENABLE_PRIVACY_SETTING = 'true';
        s.SHOWPRESENCE = showPresence.value === 'true'
        s.SAVESEMESTERS = saveSemesters.value === 'true'
        s.ALLOWCUSTOMDATES = allowCustomDates?.value === 'true'
        s.DEFAULTPERIODTYPE = defaultPeriodType?.value
        s.CURRENTOFFSET = offset;
        setSettings(s);

        setGroups([
          'Attender_Admins'?.toLowerCase()?.replace(/\s/g, ''),
          'Attender_Teacher'?.toLowerCase()?.replace(/\s/g, ''),
          'Attender_Students'?.toLowerCase()?.replace(/\s/g, ''),
          'Attender_CoinAdmin'?.toLowerCase()?.replace(/\s/g, ''),
        ]);

        setClientData({
          UserName: 'DEMO',
          Domain: 'aroscloud.dk',
          RedirectDomain: 'aroscloud.dk',
          RedeemGroupName: 'Attender_CoinAdmin'?.toLowerCase()?.replace(/\s/g, ''),
          RedeemGroupNameSrc: 'Attender_CoinAdmin',
          StudentGroupNameSrc: 'Attender_Students',
          StudentGroupName: 'Attender_Students'?.toLowerCase()?.replace(/\s/g, ''),
          RegisterGroupNameSrc: 'Attender_Teacher',
          RegisterGroupName: 'Attender_Teacher'?.toLowerCase()?.replace(/\s/g, ''),
          AdminGroupNameSrc: 'Attender_Admins',
          AdminGroupName: 'Attender_Admins'?.toLowerCase()?.replace(/\s/g, ''),
          RewardShopClerkGroupNameSrc: "RewardShopClerk",
          RewardShopClerkGroupName: "RewardShopClerk".toLowerCase()?.replace(/\s/g, ''),
          RewardAdminGroupName: "RewardAdmin".toLowerCase()?.replace(/\s/g, ''),
          RewardAdminGroupNameSrc: "RewardAdminGroup",
          CaseWorkerGroupName: "CaseWorker"?.toLowerCase()?.replace(/\s/g, ''),
          CaseWorkerGroupNameSrc: "CaseWorker",
          GuardianGroupName: "Guardian"?.toLowerCase()?.replace(/\s/g, ''),
          GuardianGroupNameSrc: "Guardian",
          SuperAdminGroupName: "SuperAdmin"?.toLowerCase()?.replace(/\s/g, ''),
          SuperAdminGroupNameSrc: "SuperAdmin",
          TeacherTitle: { en: 'Teacher', da: 'Underviser' },
          StudentTitle: { en: 'Student', da: 'Studerende' },
          RegisterDaysBack: 14,
          AdminRegisterDaysBack: 90,
          LASTLOGIN: new Date().toISOString(),
          SchoolLeaderGroupName: "Leader"?.toLowerCase()?.replace(/\s/g, ''),
          SchoolSecretaryGroupName: "Secretary"?.toLowerCase()?.replace(/\s/g, ''),
          SchoolLeaderGroupNameSrc: "Leader",
          SchoolSecretaryGroupNameSrc: "Secretary",
          GuestTeacherGroupName: "GuestTeacher"?.toLowerCase()?.replace(/\s/g, ''),
          GuestTeacherGroupNameSrc: "GuestTeacher"
        } as Client);

        setWhoIsLoggedIn('Demo', 'admin');
        setUser({ name: 'Demo User', Username: 'demo@aroscloud.dk' });

        if (!sessionStorage.getItem('demo')) {
          sessionStorage.setItem('demo', '1');
          seterror({
            code: 409,
            title: 'login-error-title',
            message: 'login-error-message',
          });
        }

        return;
      }

      if (!clientData) {
        seterror({
          code: 405,
          title: 'login-error-title',
          message: 'login-error-message',
        });
        return;
      }

      const {
        UserName,
        Domain,
        RedirectDomain,
        AdminGroupName,
        StudentGroupName,
        RegisterGroupName,
        RedeemGroupName,
        QR_OnlyLateGroup,
        RewardAdminGroupName,
        RewardShopClerkGroupName,
        CaseWorkerGroupName,
        GuardianGroupName,
        GuardianEditorGroup,
        LASTLOGIN,
        client_id,
        adhoc_o365group,
        SuperAdminGroupname,
        CustomSearchSP,
        UseGamificationPerDepartment,
        SchoolLeaderGroupName,
        SchoolSecretaryGroupName,
        GuestTeacherGroupName,
        CustomReportUIGroupName,
        GuestTeacherOnlyGroup
      } = clientData;



      let teacherDaysBack = sysSettings.find((s) => s.APP_Setting_ID === 10)?.value;
      let adminDaysBack = sysSettings.find((s) => s.APP_Setting_ID === 13)?.value;

      let RegisterDaysBack = Number(teacherDaysBack);
      let AdminRegisterDaysBack = Number(adminDaysBack);

      if (isNaN(RegisterDaysBack)) RegisterDaysBack = 14;
      if (isNaN(AdminRegisterDaysBack)) AdminRegisterDaysBack = 30;

      const client: Client = {
        UserName,
        Domain,
        RedirectDomain: RedirectDomain,
        StudentGroupName: StudentGroupName?.toLowerCase()?.replace(/\s/g, ''),
        RegisterGroupName: RegisterGroupName?.toLowerCase()?.replace(/\s/g, ''),
        RedeemGroupName: RedeemGroupName?.toLowerCase()?.replace(/\s/g, ''),
        RedeemGroupNameSrc: RedeemGroupName,
        StudentGroupQROnly: QR_OnlyLateGroup?.toLowerCase()?.replace(/\s/g, ''),
        StudentGroupQROnlySrc: QR_OnlyLateGroup,
        StudentGroupNameSrc: StudentGroupName,
        RegisterGroupNameSrc: RegisterGroupName,
        AdminGroupNameSrc: AdminGroupName,
        RewardShopClerkGroupNameSrc: RewardShopClerkGroupName,
        RewardShopClerkGroupName: RewardShopClerkGroupName?.toLowerCase()?.replace(/\s/g, ''),
        RewardAdminGroupName: RewardAdminGroupName?.toLowerCase()?.replace(/\s/g, ''),
        RewardAdminGroupNameSrc: RewardAdminGroupName,
        AdminGroupName: AdminGroupName?.toLowerCase()?.replace(/\s/g, ''),
        CaseWorkerGroupName: CaseWorkerGroupName?.toLowerCase()?.replace(/\s/g, ''),
        CaseWorkerGroupNameSrc: CaseWorkerGroupName,
        GuardianGroupName: GuardianGroupName?.toLowerCase()?.replace(/\s/g, ''),
        GuardianGroupNameSrc: GuardianGroupName,
        GuardianEditorGroupName: GuardianEditorGroup?.toLowerCase()?.replace(/\s/g, ''),
        GuardianEditorGroupNameSrc: GuardianEditorGroup,
        TeacherTitle: { en: 'Teacher', da: 'Underviser' },
        StudentTitle: { en: 'Student', da: 'Studerende' },
        RegisterDaysBack,
        AdminRegisterDaysBack,
        LASTLOGIN,
        adhoc_o365group: adhoc_o365group,
        client_id: client_id,
        SuperAdminGroupName: SuperAdminGroupname?.toLowerCase()?.replace(/\s/g, ''),
        SuperAdminGroupNameSrc: SuperAdminGroupname,
        CustomSearchSP: CustomSearchSP,
        UseGamificationPerDepartment: UseGamificationPerDepartment,
        SchoolLeaderGroupName: SchoolLeaderGroupName?.toLowerCase()?.replace(/\s/g, ''),
        SchoolSecretaryGroupName: SchoolSecretaryGroupName?.toLowerCase()?.replace(/\s/g, ''),
        SchoolLeaderGroupNameSrc: SchoolLeaderGroupName,
        SchoolSecretaryGroupNameSrc: SchoolSecretaryGroupName,
        GuestTeacherGroupName: GuestTeacherGroupName?.toLowerCase()?.replace(/\s/g, ''),
        GuestTeacherGroupNameSrc: GuestTeacherGroupName,
        CustomReportUIGroupName: CustomReportUIGroupName?.toLowerCase()?.replace(/\s/g, ''),
        CustomReportUIGroupNameSrc: CustomReportUIGroupName,
        GuestTeacherOnlyGroup: GuestTeacherOnlyGroup?.toLowerCase()?.replace(/\s/g, ''),
        GuestTeacherOnlyGroupSrc: GuestTeacherOnlyGroup
      };

      setClientData(client);

      // Mobile's user/session comes entirely from the native MsAuthPlugin login, with
      // access_token already on `user` - every mobile call site already uses
      // `mobile ? user.access_token : token` specifically to avoid needing this. The MSAL.js
      // `instance` here has never actually authenticated anything on mobile (that happened
      // natively), so acquireTokenSilent(user) always fails and falls back to
      // acquireTokenRedirect, which opens a stray browser window - this block is desktop/teams
      // only.
      if (!mobile) {
        if (settings && settings.TEAMS && settings.TEAMS === 'true') {
          const tokenRes = await authenticator.getToken(scopes);
          settoken(tokenRes);
        } else {
          if (user.nin) return
          const tokenRes = await getAccessToken(user);
          settoken(tokenRes);
        }
      }
    }
  }

  useEffect(() => {
    if (user && user.username !== 'demo@aroscloud.dk' && (!settings || (settings && !settings.DEMO_ENV))) {
      getClientData();
    }
  }, [user]);

  const getSavedFilters = async (username) => {
    let filters = (await db.GetFilters4user(username)) ?? [] // no answer: keep the default filters
    let f = JSON.parse(JSON.stringify(schoolViewFilter))
    let dashboardRange = null

    for (let i = 0; i < filters.length; i++) {
      const element = filters[i];
      let json;
      switch (element.filter_id) {
        case 1:
          f.percentage1 = Number(element.value)
          break;
        case 2:
          f.percentage2 = Number(element.value)
          break;
        case 3:
          f.percentage3 = Number(element.value)
          break;
        case 4:
          f.courses1 = element.value.split(',')
          break;
        case 5:
          f.courses3 = element.value.split(',')
          break;
        case 6:
          json = JSON.parse(element.value)
          f.fromDate0 = moment(json.fromDate).toDate()
          f.toDate0 = moment(json.toDate).toDate()
          f.quickfilter0 = json.quickFilter
          dashboardRange = { fromDate0: f.fromDate0, toDate0: f.toDate0 }
          break;
        case 8:
          json = JSON.parse(element.value)
          f.fromDate4 = moment(json.fromDate).toDate()
          f.toDate4 = moment(json.toDate).toDate()
          f.quickfilter4 = json.quickFilter
          break;
        case 9:
          f.types = element.value.split(',')
          break;
        case 10:
          json = JSON.parse(element.value)
          f.active = json.active
          f.inactive = json.inactive
          break;
        case 11:
          f.tags = element.value.split(',')
          f.tags = f.tags.map(t => Number(t))
          break;
        case 12:
          json = JSON.parse(element.value)
          f.quickdays = json.quickdays
          f.quickfilter3 = json.quickFilter
          break;
        case 15:
          json = JSON.parse(element.value)
          f.advanced = json
          break;
      }

      /*
      case 6:
        await saveFilter(11, value.map(t => t.tag_id))
        f.tags = value
        break;
      */
    }
    setSchoolViewFilter(f)
    // The dashboard reads its custom range from schoolViewDateRange (FilterNav sets both when a
    // range is applied); restoring only schoolViewFilter left it on "today" after a reload.
    if (dashboardRange) setSchoolViewDateRange(dashboardRange)
  }

  // Returns false when the settings couldn't be loaded (the error page is already showing).
  async function setupUserSettings(username, clientId, domain): Promise<boolean> {
    let cSettings = await loadLoginData(() => db.getAllClientSetting(clientId));
    if (!cSettings) {
      loginDataUnavailable('getAllClientSetting');
      return false;
    }
    let userSettings = await loadLoginData(() => db.getUserSetting(username + '@' + domain));
    if (!userSettings) {
      loginDataUnavailable('getUserSetting');
      return false;
    }
    getSavedFilters(username)
    for (let i = 0; i < userSettings.length; i++) {
      let defaultSetting = cSettings.find((c) => c.APP_Setting_ID === userSettings[i].setting_id);

      if (defaultSetting) {
        if ((defaultSetting.read_only || defaultSetting.hidden) && userSettings[i].setting_id !== 16)
          await db.setUserSetting(username + '@' + domain, userSettings[i].setting_id, userSettings[i].value, true);
        else {
          if (userSettings[i].setting_id === 16) {
            let availableLanguages = [];
            let l = cSettings.find((s) => s.APP_Setting_ID === 18);
            if (l && l.value) availableLanguages = l.value.split(';');
            if (availableLanguages.includes(userSettings[i].value)) {
              defaultSetting.value = userSettings[i].value;
            } else {
              await db.setUserSetting(username + '@' + domain, userSettings[i].setting_id, userSettings[i].value, true);
            }
          } else {
            defaultSetting.value = userSettings[i].value;
          }
        }
      }
    }

    let uSettings = cSettings.filter((s) => !s.system);
    let sysSettings = cSettings.filter((s) => s.system);

    let language = sysSettings.find((s) => s.APP_Setting_ID === 16);
    let naming = sysSettings.find((s) => s.APP_Setting_ID === 48);
    setCallNameSetting(naming.value);
    let targetLanguage = settings?.DEMO === 'true' || settings?.DEMO === true ? 'da' : language.value;
    await ensureLanguageLoaded(i18n, targetLanguage);
    await i18n.changeLanguage(targetLanguage);

    let now = moment(moment().format('YYYY-MM-DD'));
    let semesters = [
      {
        text: 'filter-text-all',
        year: null,
        tooltip: 'filter-text-off-tooltip',
        fromDate: null,
        toDate: null,
      },
    ];

    let periodType = sysSettings.find((s) => s.APP_Setting_ID === 39);
    if (periodType.value === '1' || periodType.value === '3') {
      let springStartValue = sysSettings.find((s) => s.APP_Setting_ID === 24).value;
      let springEndValue = sysSettings.find((s) => s.APP_Setting_ID === 27).value;
      let fallStartValue = sysSettings.find((s) => s.APP_Setting_ID === 28).value;
      let fallEndValue = sysSettings.find((s) => s.APP_Setting_ID === 29).value;

      if (springStartValue.length === 5) springStartValue = now.year() + '-' + springStartValue;
      else springStartValue = now.year() + '-' + moment(springStartValue).format('MM-DD');
      if (springEndValue.length === 5) springEndValue = now.year() + '-' + springEndValue;
      else springEndValue = now.year() + '-' + moment(springEndValue).format('MM-DD');

      if (fallStartValue.length === 5) fallStartValue = now.year() + '-' + fallStartValue;
      else fallStartValue = now.year() + '-' + moment(fallStartValue).format('MM-DD');
      if (fallEndValue.length === 5) fallEndValue = (now.year()) + '-' + fallEndValue;
      else fallEndValue = now.year() + '-' + moment(fallEndValue).format('MM-DD');


      let springstart = moment(springStartValue);
      let springend = moment(springEndValue);
      let fallstart = moment(fallStartValue);
      let fallend = moment(fallEndValue);

      if (springstart.isAfter(springend)) {
        springend = springend.add(1, 'year')
        springEndValue = springend.format('YYYY-MM-DD')
      }

      if (fallstart.isAfter(fallend)) {
        fallend = fallend.add(1, 'year')
        fallEndValue = fallend.format('YYYY-MM-DD')
      }


      if (fallStartValue.length === 5) fallStartValue = now.year() + '-' + fallStartValue;
      else fallStartValue = now.year() + '-' + moment(fallStartValue).format('MM-DD');

      if (fallEndValue.length === 5) fallEndValue = now.year() + '-' + fallEndValue;
      else fallEndValue = now.year() + '-' + moment(fallEndValue).format('MM-DD');

      let springfirst = false;

      if (now.isBefore(springstart)) {
        fallStartValue = now.year() - 1 + '-' + moment(fallStartValue).format('MM-DD');
        fallEndValue = now.year() + '-' + moment(fallEndValue).format('MM-DD');
        springStartValue = now.year() - 1 + '-' + moment(springStartValue).format('MM-DD');
        springEndValue = now.year() - 1 + '-' + moment(springEndValue).format('MM-DD');
        springfirst = true;
      } else if (now.isBefore(fallstart)) {
        fallStartValue = now.year() - 1 + '-' + moment(fallStartValue).format('MM-DD');
        fallEndValue = now.year() + '-' + moment(fallEndValue).format('MM-DD');
        springfirst = false;
      } else if (now.isAfter(springend)) {
        fallStartValue = now.year() + '-' + moment(fallStartValue).format('MM-DD');
        fallEndValue = now.year() + 1 + '-' + moment(fallEndValue).format('MM-DD');
        springfirst = true;
      }
      else {
        fallStartValue = now.year() - 1 + '-' + moment(fallStartValue).format('MM-DD');
        fallEndValue = now.year() + '-' + moment(fallEndValue).format('MM-DD');
        springfirst = false;
      }

      springstart = moment(springStartValue);
      springend = moment(springEndValue);
      fallstart = moment(fallStartValue);
      fallend = moment(fallEndValue);

      let spring = {
        text: 'spring',
        year: springstart.year().toString(),
        tooltip: 'spring-tooltip',
        fromDate: springstart.format('YYYY-MM-DD'),
        toDate: springend.format('YYYY-MM-DD'),
      };

      let fall = {
        text: 'fall',
        year: fallstart.year().toString(),
        tooltip: 'fall-tooltip',
        fromDate: fallstart.format('YYYY-MM-DD'),
        toDate: fallend.format('YYYY-MM-DD'),
      };

      if (springfirst) {
        semesters.push(spring);
        semesters.push(fall);
      } else {
        semesters.push(fall);
        semesters.push(spring);
      }
    }
    if (periodType.value === '2' || periodType.value === '3') {
      let yearStartValue = sysSettings.find((s) => s.APP_Setting_ID === 30).value;
      let yearEndValue = sysSettings.find((s) => s.APP_Setting_ID === 31).value;

      if (yearStartValue.length <= 5) yearStartValue = now.year() + '-' + yearStartValue;
      if (yearEndValue.length <= 5) yearEndValue = now.year() + 1 + '-' + yearEndValue;
      yearStartValue = now.year() + '-' + moment(yearStartValue).format('MM-DD');
      yearEndValue = now.year() + 1 + '-' + moment(yearEndValue).format('MM-DD');

      if (moment(yearStartValue).isAfter(moment())) {
        yearStartValue = moment(yearStartValue).add(-1, 'year').format('YYYY-MM-DD')
        yearEndValue = moment(yearEndValue).add(-1, 'year').format('YYYY-MM-DD')
      }

      let yearstart = moment(yearStartValue)
      let yearend = moment(yearEndValue)

      let previousYearStart = moment(yearstart).add('-1', 'years');
      let previousYearEnd = moment(yearend).add('-1', 'years');

      let semesterText = previousYearStart.year() === previousYearEnd.year() ? previousYearStart.year().toString() : previousYearStart.year().toString() + '/' + previousYearEnd.year().toString();

      semesters.push({
        text: semesterText,
        year: (now.year() - 1).toString(),
        tooltip: semesterText,
        fromDate: previousYearStart.format('YYYY-MM-DD'),
        toDate: previousYearEnd.format('YYYY-MM-DD'),
      });

      semesterText = yearstart.year() === yearend.year() ? yearstart.year().toString() : yearstart.year().toString() + '/' + yearend.year().toString();

      semesters.push({
        text: semesterText,
        year: now.year().toString(),
        tooltip: semesterText,
        fromDate: yearstart.format('YYYY-MM-DD'),
        toDate: yearend.format('YYYY-MM-DD'),
      });
    }
    let semesterSetting = userSettings.find((s) => s.setting_id === 25);
    if (semesterSetting) setSelectedSemester(semesterSetting.value)
    setSemesters(semesters);

    for (let i = 0; i < uSettings.length; i++) {
      switch (uSettings[i].APP_Setting_ID) {
        case 1:
          setPersistentStore((draft) => {
            draft.pictures = uSettings[i].value === 'true';
          });
          break;
        case 2:
          setPersistentStore((draft) => {
            draft.privacyEnabled = username === "demouser" ? false : uSettings[i].value === 'true';
          });
          break;
        case 3:
          setPersistentStore((draft) => {
            draft.studentSort = uSettings[i].value === 'A-Z' ? 'alpha-ascending' : 'alpha-descending';
          });
          break;
        case 5:
          setPersistentStore((draft) => {
            draft.tooltips = uSettings[i].value.toLowerCase() === 'true';
          });
          break;
        case 14:
          setPersistentStore((draft) => {
            draft.viewColoring = uSettings[i].value === 'Use colours' ? ViewColoring.DEFAULT : ViewColoring.CONTRAST;
          });
          break;
        case 15:
          setPersistentStore((draft) => {
            let temp = uSettings.find((t) => t.APP_Setting_ID === 14);
            switch (uSettings[i].value) {
              case 'Colour':
              case 'Color':
                draft.viewIndicators = temp.value !== 'Use colours' ? ViewIndicators.ICONS : ViewIndicators.COLOR;
                break;
              case 'Icons':
                draft.viewIndicators = ViewIndicators.ICONS;
                break;
              case 'Color and icons':
                draft.viewIndicators = temp.value !== 'Use colours' ? ViewIndicators.ICONS : ViewIndicators.COLOR_AND_ICONS;
                break;
              case 'Color and contrast icons':
                draft.viewIndicators = temp.value !== 'Use colours' ? ViewIndicators.ICONS : ViewIndicators.COLOR_AND_CONTRAST_ICONS;
                break;
            }
          });
          break;
        case 102:
          setPersistentStore((draft) => {
            draft.defaultQr = uSettings[i].value === 'true';
          });
          break;
      }
    }

    setClientSettings(uSettings);
    setSystemSettings(sysSettings);
    return true;
  }

  // Had real behavioral drift, not just incidental differences (see git log /
  // conversation history for the full comparison). Reconciled onto desktop's
  // fuller behavior throughout - kohort check active, all roles pushed into
  // groupNameArr for the MergeLogin backend sync, demo-mode guard applied -
  // except the tutorial-display block, which used mobile's live version
  // (desktop's called setTutorial with the result commented out, i.e. never
  // actually applied it) combined with desktop's APP_Setting_ID===51 toggle
  // check gating whether to fetch/show it at all.
  async function assignGroups() {
    OnScreenDebugger.instance.log('assign groups new');

    let groupJson = {}

    //@ts-ignore
    if (client.GuardianGroupNameSrc) groupJson.guardian = client.GuardianGroupNameSrc;
    //@ts-ignore
    if (client.StudentGroupNameSrc) groupJson.student = client.StudentGroupNameSrc;
    //@ts-ignore
    if (client.StudentGroupQROnlySrc) groupJson.studentQROnly = client.StudentGroupQROnlySrc;
    //@ts-ignore
    if (client.RegisterGroupNameSrc) groupJson.teacher = client.RegisterGroupNameSrc;
    //@ts-ignore
    if (client.AdminGroupNameSrc) groupJson.admin = client.AdminGroupNameSrc;
    //@ts-ignore
    if (client.RedeemGroupNameSrc) groupJson.redeem = client.RedeemGroupNameSrc;
    //@ts-ignore
    if (client.SuperAdminGroupNameSrc) groupJson.superAdmin = client.SuperAdminGroupNameSrc;
    //@ts-ignore
    if (client.RewardAdminGroupNameSrc) groupJson.rewardAdmin = client.RewardAdminGroupNameSrc;
    //@ts-ignore
    if (client.RewardShopClerkGroupNameSrc) groupJson.rewardShopClerk = client.RewardShopClerkGroupNameSrc;
    //@ts-ignore
    if (client.CaseWorkerGroupNameSrc) groupJson.caseWorker = client.CaseWorkerGroupNameSrc;
    //@ts-ignore
    if (client.SchoolLeaderGroupNameSrc) groupJson.schoolLeader = client.SchoolLeaderGroupNameSrc;
    //@ts-ignore
    if (client.SchoolSecretaryGroupNameSrc) groupJson.schoolSecretary = client.SchoolSecretaryGroupNameSrc;
    //@ts-ignore
    if (client.CustomReportUIGroupNameSrc) groupJson.customReportUI = client.CustomReportUIGroupNameSrc;
    //@ts-ignore
    if (client.GuestTeacherOnlyGroupSrc) groupJson.guestTeacherOnly = client.GuestTeacherOnlyGroupSrc;
    //@ts-ignore
    if (client.GuardianEditorGroupNameSrc) groupJson.guardianEditor = client.GuardianEditorGroupNameSrc;

    // The Graph $batch call returns nothing (callGraphPost's null) when it fails - expired token,
    // dropped connection, Graph hiccup - and reading r.responses then crashed here before
    // setGroups(), leaving the user on the loader for good. Retry once (callGraphPost renews the
    // token first); if it still fails, show the login error instead of hanging.
    let r = await graph.GetGroupMemberships(mobile ? user.access_token : token, groupJson);
    if (!r?.responses) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      // From the store, not this closure's `token`: if that was renewed meanwhile, the stale one
      // wouldn't match the store's and callGraphPost's freshen() would pass it through untouched.
      const latest = useStore.getState();
      r = await graph.GetGroupMemberships(mobile ? latest.user?.access_token : latest.token, groupJson);
    }
    if (!r?.responses) {
      const error = new Error('Graph group membership lookup failed');
      error.name = 'GroupMembershipLookupFailed';
      Bugsnag.notify(error, (event) => {
        event.severity = 'warning';
      });
      seterror({
        code: 503,
        title: 'login-error-title',
        message: 'login-error-message',
      });
      return;
    }

    if (process.env.ENV === 'DEV') {
      let kohort = true;
      let kohortCheck = await db.checkifkohortexists();
      if (kohortCheck?.length > 0 && kohortCheck[0].kohortactive) {
        kohort = true;
      } else {
        kohort = false;
      }
      setKohortsAvailable(kohort)
    }

    let userInfo = await db.getUserInfo(client.UserName);
    let count = await db.GetAttCounter4User(client.UserName);
    if (count && count.length > 0) setNotificationCounter(count[0].attCount)

    if (userInfo?.length > 0) {
      if (userInfo[0].student_id && settings.DEMO !== 'true' && settings.DEMO !== true) {
        let dates = await db.GetAllLessonDays4Student(
          userInfo[0].student_id,
          moment().format('YYYY-MM-DD'),
          moment().add(14, 'days').format('YYYY-MM-DD'),
        );
        if (dates?.length > 0) setHideAbsenceMenu(false);
      }

      if (client.UseGamificationPerDepartment) {
        if (userInfo[0].department !== null && userInfo[0].department !== undefined && userInfo[0].department.length > 0) {
          let departmentSettings = await db.getDepartmentSettings(userInfo[0].department);
          if (departmentSettings?.length > 0) {
            // Latest from the store: assignGroups can start while `settings` was still the basic
            // object, and writing that copy back wiped getClientData's complete settings.
            let s = JSON.parse(JSON.stringify(useStore.getState().settings ?? settings));
            s.USECOINS = departmentSettings[0].coins;
            s.USEBADGES = departmentSettings[0].badges;
            s.USESTREAKS = departmentSettings[0].streaks;
            s.USEREWARDS = departmentSettings[0].rewards;
            setSettings(s);
          }
        }
      }

      setIds(userInfo[0]);
    }

    const groupArr = [];
    let groupNameArr = [];

    //@ts-ignore
    let userinfo = await graph.getSelf(mobile ? user.access_token : token);
    //@ts-ignore
    let firstname = userinfo?.givenName ? userinfo?.givenName : '';
    //@ts-ignore
    let lastname = userinfo?.surname ? userinfo?.surname : '';
    //@ts-ignore
    let username = userinfo?.userPrincipalName ? userinfo?.userPrincipalName.split('@')[0] : '';
    //@ts-ignore
    let name = userinfo?.displayName ? userinfo?.displayName : '';
    let mail = userinfo?.mail ? userinfo?.mail : '';

    if (r.responses.length > 0) {
      r.responses.forEach((response) => {

        if (response.body.value && response.body.value.length > 0) {
          {
            console.log(`Group found: ${response.id} for user ${user.username}`);
            switch (response.id) {
              case "guardian":
                groupArr.push(client.GuardianGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                break;
              case "student":
                groupArr.push(client.StudentGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                break;
              case "studentQROnly":
                groupArr.push(client.StudentGroupQROnlySrc.toLowerCase().replace(/\s/g, ''));
                break;
              case "teacher":
                groupArr.push(client.RegisterGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.RegisterGroupNameSrc);
                break;
              case "admin":
                groupArr.push(client.AdminGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.AdminGroupNameSrc);
                break;
              case "redeem":
                groupArr.push(client.RedeemGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.RedeemGroupNameSrc);
                break;
              case "superAdmin":
                groupArr.push(client.SuperAdminGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.SuperAdminGroupNameSrc);
                break;
              case "rewardAdmin":
                groupArr.push(client.RewardAdminGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.RewardAdminGroupNameSrc);
                break;
              case "rewardShopClerk":
                groupArr.push(client.RewardShopClerkGroupName.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.RewardShopClerkGroupName);
                break;
              case "caseWorker":
                groupArr.push(client.CaseWorkerGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                break;
              case "schoolLeader":
                groupArr.push(client.SchoolLeaderGroupName.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.SchoolLeaderGroupName);
                break;
              case "schoolSecretary":
                groupArr.push(client.SchoolSecretaryGroupName.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.SchoolSecretaryGroupName);
                break;
              case "customReportUI":
                groupArr.push(client.CustomReportUIGroupName.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.CustomReportUIGroupName);
                break;
              case "guestTeacherOnly":
                groupArr.push(client.GuestTeacherOnlyGroup.toLowerCase().replace(/\s/g, ''));
                groupNameArr.push(client.GuestTeacherOnlyGroup);
                break;
              case "guardianEditor":
                groupArr.push(client.GuardianEditorGroupNameSrc.toLowerCase().replace(/\s/g, ''));
                break;
            }
          }
        }
      })
    }

    let caseworkerGroup = client.CaseWorkerGroupNameSrc?.toLowerCase().replace(/\s/g, '');
    let caseworker = caseworkerGroup && groupArr.find((g) => g === caseworkerGroup);

    if (caseworker) {
      let userinfo = await graph.getSelf(mobile ? user.access_token : token)
      if (userinfo && userinfo.jobTitle) {
        let id = userinfo.jobTitle.split('Caseworker|')
        let email = userinfo.officeLocation;

        if (id.length > 1) {
          setCaseWorkerId(id[1])
          setCaseWorkerEmail(email)
        }
      }
    }

    if (groupNameArr.length > 0) {
      await db.MergeLogin(userinfo.id, username, userinfo?.userPrincipalName, firstname, lastname, name, groupNameArr.join(';'), mail);
    }

    if (!settings.DEMO) {
      setGroups(groupArr);
      // The name shown as "logged in as". getProfile can come back empty or fail (no token - it couldn't
      // be renewed - or Graph not answering), which used to crash here as an unhandled error: the old
      // try/catch wrapped only the .then() call, not what ran inside it later. Then fall back to the name
      // from the Microsoft login, as the guardian path does.
      const showLoginName = () => setWhoIsLoggedIn(user?.name ?? '', '');
      getProfile()
        .then((profile) => {
          if (!profile) return showLoginName();
          if (profile.givenName || profile.surname) setWhoIsLoggedIn(profile.givenName ?? '', profile.surname ?? '');
          else setWhoIsLoggedIn(profile.displayName ?? user?.name ?? '', '');
        })
        .catch(showLoginName);
      if (groupArr.length === 0) {
        setTutorial(false);
        seterror({
          code: 403,
          title: 'not-authorized-title',
          message: 'not-authorized-message',
        });
      } else {
        setCookie('msal_username', user.username, 7);
        localStorage.setItem('login', 'true');
        setCookie('login', 'true', 14);
        let showTutorial = systemSettings.find((t) => t.APP_Setting_ID === 51);
        if (showTutorial && showTutorial.value.toLowerCase() === 'true') {
          let tutorial = await db.getClientTutorialOptions(user.username);
          if (tutorial?.[0]) setTutorial(tutorial[0].showstartupscreen);
        }
      }
    }
  }

  // Was near-duplicated between desktop/mobile with only incidental drift
  // (localStorage vs sessionStorage for one key, an unawaited settings clone
  // vs an awaited one, a couple of fields only one side set). Consolidated
  // here; setTutorial is the one deliberate behavior difference left, so it's
  // gated on `mobile` instead of living in two copies of this function.
  async function getDemoData() {
    sessionStorage.setItem('api_url', 'https://wsattenderdemosite.azurewebsites.net');
    sessionStorage.setItem('demo', '1');
    let initialRegistrationTypes = await db.GetInitialsRegistrations_Types();
    if (initialRegistrationTypes) setInitialRegistrationTypes(initialRegistrationTypes);
    // The demo's own client (3) - it used to borrow client 14's settings, so changes made for that
    // school changed the demo too. The demo still forces some of them on below (coins, badges...).
    if (!(await setupUserSettings('demouser', DEMO_CLIENT_ID, 'arosdemo.dk'))) return;
    let s = JSON.parse(JSON.stringify(settings));
    let offset = parseInt('+2:00'.split(':')[0]) * 60 + parseInt('+2:00'.split(':')[1]);
    s.IMAGE_SAS_TOKEN = '?sv=2020-08-04&ss=bfqt&srt=co&sp=rwdlacupitfx&se=2029-12-22T18:47:14Z&st=2021-12-22T10:47:14Z&spr=https&sig=BSqUEkBabB7dxCw8nThyZxqemGGDkMs%2F7Olk4b9vuyw%3D';
    s.IMAGE_URL = 'https://ImagesDEMO.azureedge.net/thumbnails';
    s.USER_IMAGE_URL = 'https://ImagesDEMO.azureedge.net/hiresthumbnails';
    s.STUDENTNOTEHIDDEN = false;
    s.USE_LESSON_BASED_ABSENCE = 'false';
    setSchoolName('Demo School');
    s.USECOINS = true;
    s.USEBADGES = true;
    s.USESTREAKS = true;
    s.USEREWARDS = true;
    s.USEMERIT = false;
    s.USEPAUSE = false;
    s.DEMO = true;
    s.ENABLE_PRIVACY_SETTING = 'true';
    s.SHOWPRESENCE = false;
    s.COLORSETTINGS = { low: '5', medium: '10', high: '15' };
    s.SAVESEMESTERS = false;
    s.ALLOWCUSTOMDATES = true;
    s.CURRENTOFFSET = offset;
    s.client_id = DEMO_CLIENT_ID;
    setSettings(s);
    setHideAbsenceMenu(false);
    setSelectedCaseworker('Demo caseworker');
    setGroups([
      'Attender_Admins'?.toLowerCase()?.replace(/\s/g, ''),
      'Attender_CoinAdmin'?.toLowerCase()?.replace(/\s/g, ''),
      'RewardShopClerk'.toLowerCase()?.replace(/\s/g, ''),
      'RewardAdmin'.toLowerCase()?.replace(/\s/g, ''),
    ]);

    setClientData({
      UserName: 'DEMO',
      Domain: 'aroscloud.dk',
      RedirectDomain: 'aroscloud.dk',
      RedeemGroupName: 'Attender_CoinAdmin'?.toLowerCase()?.replace(/\s/g, ''),
      RedeemGroupNameSrc: 'Attender_CoinAdmin',
      StudentGroupNameSrc: 'Attender_Students',
      StudentGroupQROnly: 'QR_only'?.toLowerCase()?.replace(/\s/g, ''),
      StudentGroupName: 'Attender_Students'?.toLowerCase()?.replace(/\s/g, ''),
      StudentGroupQROnlySrc: 'QR_only',
      RegisterGroupNameSrc: 'Attender_Teacher',
      RegisterGroupName: 'Attender_Teacher'?.toLowerCase()?.replace(/\s/g, ''),
      AdminGroupNameSrc: 'Attender_Admins',
      AdminGroupName: 'Attender_Admins'?.toLowerCase()?.replace(/\s/g, ''),
      RewardShopClerkGroupNameSrc: 'RewardShopClerk',
      RewardShopClerkGroupName: 'RewardShopClerk'.toLowerCase()?.replace(/\s/g, ''),
      RewardAdminGroupName: 'RewardAdmin'.toLowerCase()?.replace(/\s/g, ''),
      RewardAdminGroupNameSrc: 'RewardAdminGroup',
      CaseWorkerGroupName: 'CaseWorker'?.toLowerCase()?.replace(/\s/g, ''),
      CaseWorkerGroupNameSrc: 'CaseWorker',
      GuardianGroupName: 'Guardian'?.toLowerCase()?.replace(/\s/g, ''),
      GuardianGroupNameSrc: 'Guardian',
      SuperAdminGroupName: 'SuperAdmin'?.toLowerCase()?.replace(/\s/g, ''),
      SuperAdminGroupNameSrc: 'SuperAdmin',
      TeacherTitle: { en: 'Teacher', da: 'Underviser' },
      StudentTitle: { en: 'Student', da: 'Studerende' },
      RegisterDaysBack: 14,
      AdminRegisterDaysBack: 90,
      LASTLOGIN: new Date().toISOString(),
      SchoolLeaderGroupName: 'Leader'?.toLowerCase()?.replace(/\s/g, ''),
      SchoolSecretaryGroupName: 'Secretary'?.toLowerCase()?.replace(/\s/g, ''),
      SchoolLeaderGroupNameSrc: 'Leader',
      SchoolSecretaryGroupNameSrc: 'Secretary',
      GuestTeacherGroupName: 'GuestTeacher'?.toLowerCase()?.replace(/\s/g, ''),
      GuestTeacherGroupNameSrc: 'GuestTeacher',
    } as Client);

    setWhoIsLoggedIn('Demo', 'admin');
    setUser({ name: 'Demo User', Username: 'demo@aroscloud.dk' });

    if (!sessionStorage.getItem('demo')) {
      sessionStorage.setItem('demo', '1');
    }
    if (mobile) setTutorial(true);
    setDemo(true);
  }

  return {
    // flags passed straight through so provider components don't need to redeclare them
    mobile, isDemo, teams, isMultiLogin,
    // local state
    demo, setDemo, error, seterror, scopes, router,
    authenticator, setAuthenticator, i18n, loggingIn, setlogginIn, setPersistentStore,
    // store fields
    client, setClientData, setSettings, settings, setWhoIsLoggedIn, setTutorial, setGroups, groups,
    token, settoken, setMobileLogin, mobileLogin, setMenuOpen, setSafeArea, reset, setTenantId,
    setClientSettings, setSystemSettings, systemSettings, setSemesters, ids, setIds, user, setUser,
    initialRegistrationTypes, setInitialRegistrationTypes, hideAbsenceMenu, setHideAbsenceMenu,
    setCallNameSetting, setCaseWorkerId, setAlerts, caseWorkerEmail, setCaseWorkerEmail, schoolName,
    setSchoolName, setSelectedCaseworker, wondeTags, setWondeTags, multilogin, setMultiLogin,
    setOtherUser, otherUser, whoIsLoggedIn, setSchools, setGuardianStudents, guardianStudents,
    filterDate, schools, setNotificationCounter, schoolViewFilter, setSchoolViewFilter,
    selectedSemester, setSelectedSemester, kohortsAvailable, setKohortsAvailable, setPopupLogin,
    setActive, active, apiUrl, setApiUrl,
    // shared functions
    initUser, getId, getStudentId, getType, getCookie, setCookie, delete_cookie, loginMitId, getTokenTeams, signInAgain,
    getDemoData, assignGroups,
    awaitSettings, getMitidToken, getWondeTags, assignGroupsMitId,
    checkToken, getClientData,
    getSavedFilters, setupUserSettings, getAccessToken,
  };
}
